// sync-engine.js — heartbeat + outbox push + delta pull + conflict handling.
// Brief 025.
//
// Before this module the web port was a local-only client: it made no periodic
// server call (so a displaced session stayed "signed in" for up to the 2h JWT
// TTL), never pulled iOS's progress, and never pushed its own. This is the
// single place that closes all three gaps.
//
// SHAPE. One module-level singleton, because the things it owns are inherently
// singular: one heartbeat interval per tab, one outbox drain at a time, one set
// of unload listeners. `initialiseSync()` / `teardownSync()` bracket a signed-in
// session; everything else is idempotent and safe to call at any time.
//
// LAYERING. Imports crm-client (transport), learner-store (outbox + cursor),
// progress-rows (wire shapes) and sync-merge (the reducer). It imports NOTHING
// from src/screens/ or src/ui/, and touches the DOM only through explicitly
// guarded `typeof document !== "undefined"` blocks for the visibility/unload
// listeners — the same pattern learner-store already uses for its
// `kfe:item-completed` dispatch. Screens talk to it through `initialiseSync` /
// `teardownSync` and the two subscribe seams; it never calls back into a screen.
//
// CYCLE AVOIDANCE. learner-store must be able to queue rows, but sync-engine
// imports learner-store. The break is `sync-queue.js`: a zero-dependency leaf
// that learner-store imports, into which this module installs itself as the
// sink AT MODULE LOAD (bottom of the file). Load-time, not init-time, so
// progress made while signed out still reaches the durable outbox — only the
// FLUSH is gated on a live session.

import {
  crmTouchBase,
  crmAcceptLoss,
  crmAddLearnerProgress,
  crmUpdateLearnerProgress,
  crmQueryLearnerProgress,
  crmDownloadLearnerProgress,
  crmUpdateLastSync,
  crmContentUpdate,
  refreshSessionIfDue,
} from "./crm-client.js";
import { isSignedIn, REFRESH_MARGIN_MS } from "./auth-store.js";
import {
  enqueueOutboxRow,
  getOutboxRows,
  putOutboxRow,
  deleteOutboxRows,
  clearOutbox,
  getSyncState,
  setSyncState,
} from "./learner-store.js";
import * as learnerStore from "./learner-store.js";
import { setQueueSink, setBreakpointSink } from "./sync-queue.js";
import { Heartbeat, HEARTBEAT_INTERVAL_MS } from "./heartbeat.js";
import {
  buildLearnerProgress,
  buildUploadBody,
  makeRequestId,
  isSyncableTable,
  parseDownloadedProgress,
} from "./progress-rows.js";
import { mergeDownloadedProgress } from "./sync-merge.js";

// ---------------------------------------------------------------------------
// Policy constants
// ---------------------------------------------------------------------------

/**
 * Retry schedule (Brief 025 §12). Index == attempts already made, so a row that
 * has failed twice waits RETRY_DELAYS_MS[2] = 5s. Attempt 1 is immediate.
 * After MAX_ATTEMPTS the row is PAUSED — kept in the outbox, not retried, until
 * the next breakpoint flush or an explicit `flush({ reason })` un-pauses it.
 * Paused ≠ lost: the row is still the learner's intent and still ships later.
 */
export const RETRY_DELAYS_MS = [0, 2_000, 5_000, 15_000, 60_000];
export const MAX_ATTEMPTS = 5;

/**
 * How long a `queueRow` waits before flushing, so a burst of rows from one
 * completion (item + mastery + test-instance all fire within a tick) rides in
 * ONE request instead of three. Pre-empted by any breakpoint (Gotcha #4).
 */
export const QUEUE_DEBOUNCE_MS = 2_000;

/** How stale a content check may get before a tab-focus re-check (Brief 025 §13). */
export const CONTENT_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** localStorage flag the next pathway load reads to force a content re-fetch. */
export const CONTENT_STALE_KEY = "kfe_content_stale";

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

/** @type {Array<() => void>} */ let displacedListeners = [];
/** @type {Array<(payload: any) => void>} */ let conflictListeners = [];
/** @type {Array<() => void>} */ let cleanups = [];

/** @type {Heartbeat | null} */ let heartbeat = null;
/** @type {any} */ let debounceTimer = null;
/** @type {any} */ let retryTimer = null;
/** @type {Promise<any> | null} */ let inFlightFlush = null;
let initialised = false;
let lastContentCheckAt = 0;
/** The most recent 91048 payload, held for `resolveProgressConflict` (Brief 190 seam). */
/** @type {any} */ let pendingConflict = null;

/** Injectable seams — production defaults; tests override via `initialiseSync(deps)`. */
const DEFAULTS = {
  heartbeatIntervalMs: HEARTBEAT_INTERVAL_MS,
  debounceMs: QUEUE_DEBOUNCE_MS,
  /** Brief 194: how long before nominal expiry the proactive refresh fires. */
  refreshMarginMs: REFRESH_MARGIN_MS,
  /** @type {{ setTimeout: Function, clearTimeout: Function, setInterval: Function, clearInterval: Function }} */
  timers: {
    setTimeout: (/** @type {any} */ ...a) => setTimeout(...(/** @type {[any, any]} */ (a))),
    clearTimeout: (/** @type {any} */ h) => clearTimeout(h),
    setInterval: (/** @type {any} */ ...a) => setInterval(...(/** @type {[any, any]} */ (a))),
    clearInterval: (/** @type {any} */ h) => clearInterval(h),
  },
  now: () => Date.now(),
};
let deps = { ...DEFAULTS };

// ---------------------------------------------------------------------------
// Subscription seams
// ---------------------------------------------------------------------------

/**
 * Fired when ANY sync call (heartbeat, push or pull) returns a session verdict:
 * a 401/20000 whose refresh failed, or a 409/91049. Note this runs IN ADDITION
 * to crm-client's own
 * `notifySessionInvalid` cascade, which is what actually bounces the router —
 * this seam is for sync-local cleanup and for anyone who wants to know.
 * @param {() => void} cb
 * @returns {() => void} unsubscribe
 */
export function onSessionDisplaced(cb) {
  displacedListeners.push(cb);
  return () => { displacedListeners = displacedListeners.filter((c) => c !== cb); };
}

/**
 * Fired when a PUSH comes back 409/91048 — the CRM has newer unpushed progress
 * from another device. The payload is crm-client's full error body
 * (`previous_device`, `options`, `gap_seconds`, `session_resolution`, …).
 *
 * Brief 190 (device-switch modal) subscribes here and calls
 * `resolveProgressConflict(choice)` with the learner's answer. Until 190 lands,
 * the built-in fallback below logs the conflict and leaves the outbox intact.
 * @param {(payload: any) => void} cb
 * @returns {() => void} unsubscribe
 */
export function onProgressConflict(cb) {
  conflictListeners.push(cb);
  return () => { conflictListeners = conflictListeners.filter((c) => c !== cb); };
}

function emitDisplaced() {
  for (const cb of displacedListeners) {
    try { cb(); } catch (err) { console.error("[sync-engine] onSessionDisplaced listener threw", err); }
  }
}

/** @param {any} payload */
function emitConflict(payload) {
  pendingConflict = payload ?? null;
  if (conflictListeners.length === 0) {
    // Brief 188 Addendum 2 fallback. Brief 190's modal is not wired yet, so
    // there is nowhere to ask the learner Wait / Accept Loss / Reconcile. The
    // outbox is preserved untouched and the next breakpoint retries — which is
    // the "Wait" branch by default, and the only branch that is safe to pick on
    // the learner's behalf (the other two can discard progress).
    console.warn(
      "[sync-engine] 91048 progress conflict with no handler — outbox PRESERVED, " +
      "retrying on next breakpoint (Brief 190 modal not landed yet). Payload:",
      payload,
    );
    return;
  }
  for (const cb of conflictListeners) {
    try { cb(payload); } catch (err) { console.error("[sync-engine] onProgressConflict listener threw", err); }
  }
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * Start syncing for a signed-in learner. Called from `kfe-app-shell` on the
 * signed-in transition of `kfe:auth-changed`.
 *
 * Order: listeners → heartbeat → pull → flush. The pull runs BEFORE the flush
 * so a learner logging in on web sees iOS's progress first and the outbox
 * (which may have been sitting there since a previous session) ships against an
 * already-reconciled local state.
 *
 * Idempotent — a second call while initialised is a no-op, so a duplicate
 * `kfe:auth-changed` can't double-arm the heartbeat.
 *
 * @param {Partial<typeof DEFAULTS>} [overrides] test seams
 * @returns {Promise<void>} resolves once the login pull has settled
 */
export async function initialiseSync(overrides = {}) {
  if (initialised) return;
  initialised = true;
  deps = { ...DEFAULTS, ...overrides };

  heartbeat = new Heartbeat({
    beat: () => touchBase(),
    intervalMs: deps.heartbeatIntervalMs,
    timers: /** @type {any} */ (deps.timers),
  });

  installDocumentListeners();
  heartbeat.start();

  // Pull-on-login, then ship anything the outbox was already holding.
  await pull({ reason: "login" }).catch((err) => console.warn("[sync-engine] login pull failed", err));
  await checkContentUpdate().catch(() => {});
  await flush({ reason: "login" }).catch((err) => console.warn("[sync-engine] login flush failed", err));
}

/**
 * Stop syncing. Called on sign-out and on the signed-out transition of
 * `kfe:auth-changed`.
 *
 * Deliberately does NOT flush and does NOT clear the outbox. Brief 191's
 * principle is that sign-out never blocks on a server call, and an unflushed
 * row is still the learner's intent — it ships on the next login. The caller
 * (auth-store's `kfe:sync-teardown`) gets one best-effort flush attempt BEFORE
 * this runs; if it doesn't land, the rows simply wait.
 */
export function teardownSync() {
  initialised = false;
  heartbeat?.stop();
  heartbeat = null;
  if (debounceTimer !== null) { deps.timers.clearTimeout(debounceTimer); debounceTimer = null; }
  if (retryTimer !== null) { deps.timers.clearTimeout(retryTimer); retryTimer = null; }
  inFlightFlush = null;
  pendingConflict = null;
  for (const fn of cleanups) { try { fn(); } catch { /* listener already gone */ } }
  cleanups = [];
}

/** @returns {boolean} whether a signed-in sync session is currently armed */
export function isSyncing() {
  return initialised;
}

/**
 * Diagnostics for tests, the dev Settings panel and the live smoke. Never used
 * for control flow.
 * @returns {Promise<{ initialised: boolean, heartbeatRunning: boolean, outbox: number, paused: number, syncState: any }>}
 */
export async function getSyncDiagnostics() {
  const rows = await getOutboxRows();
  return {
    initialised,
    heartbeatRunning: heartbeat?.running ?? false,
    outbox: rows.length,
    paused: rows.filter((r) => r.paused).length,
    syncState: await getSyncState(),
  };
}

// ---------------------------------------------------------------------------
// Browser event wiring
// ---------------------------------------------------------------------------

/**
 * Visibility + unload listeners. Registered here rather than in the app shell
 * so their lifetime is exactly the sync session's, and so `teardownSync` is the
 * single place that removes them (Common Pitfall #1 — every subscribe gets a
 * matching unsubscribe).
 */
function installDocumentListeners() {
  if (typeof document === "undefined") return;

  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      // Last-ditch: the tab may never come back. Flush with the unload-safe
      // transport before the process is frozen.
      heartbeat?.pause();
      void flush({ reason: "visibility" }).catch(() => {});
      return;
    }
    // Back in front. Beat immediately (Allen's "plus on tab focus" — the tab may
    // have been hidden across a displacement), resume the cadence, and re-check
    // content if the last check has gone stale.
    heartbeat?.resume();
    void checkContentUpdate().catch(() => {});
  };
  document.addEventListener("visibilitychange", onVisibility);
  cleanups.push(() => document.removeEventListener("visibilitychange", onVisibility));

  if (typeof window !== "undefined") {
    const onPageHide = () => { void flush({ reason: "pagehide" }).catch(() => {}); };
    window.addEventListener("pagehide", onPageHide);
    cleanups.push(() => window.removeEventListener("pagehide", onPageHide));
  }
}

// ---------------------------------------------------------------------------
// Heartbeat / touch-base
// ---------------------------------------------------------------------------

/**
 * The two error kinds that are a verdict ABOUT THE SESSION rather than about
 * one request (Brief 194 split them): `session_expired` is a 401 + error code
 * 20000 whose refresh-and-retry already failed inside crm-client,
 * `session_displaced` is a 409 + error code 91049. Both mean "stop syncing";
 * they differ only in what the app shell then shows the learner, which is
 * crm-client's `onSessionInvalid(reason)` business, not ours.
 * @param {string | undefined} kind
 * @returns {boolean}
 */
function isSessionVerdict(kind) {
  return kind === "session_expired" || kind === "session_displaced";
}

/**
 * The proactive leg of Brief 194: renew the token BEFORE it expires, so the
 * learner never meets a 401 in the first place. Runs ahead of every touch-base
 * — i.e. on every heartbeat (60s) and before every flush — and is a no-op
 * until the token is within the margin (5 min of a 7200s token), so the common
 * case costs one `Date.now()` comparison.
 *
 * Deliberately NOT a verdict: a refresh that cannot be done (offline, or the
 * dev-bypass mock) leaves the token alone and lets the beat proceed. Only a
 * 401 that survives a refresh signs anyone out, and that decision is
 * crm-client's.
 */
async function refreshTokenIfDue() {
  try {
    await refreshSessionIfDue({ now: deps.now(), marginMs: deps.refreshMarginMs });
  } catch (err) {
    console.warn("[sync-engine] proactive refresh threw", err);
  }
}

/**
 * One touch-base. Returns whether the session is still valid, so the "around
 * each sync" callers can abort before sending rows the server would reject.
 *
 * @param {{ sessionResolution?: "wait"|"accept_loss"|"reconcile" }} [params]
 * @returns {Promise<boolean>} true when the session is live
 */
export async function touchBase(params = {}) {
  if (!isSignedIn()) return false;
  await refreshTokenIfDue();
  const res = await crmTouchBase(params);
  if (res.success) return true;
  if (isSessionVerdict(res.error?.kind)) {
    handleDisplaced();
    return false;
  }
  if (res.error?.kind === "progress_conflict") {
    // 91048 can surface on touch-base too, not just on a push (the CRM detects
    // it on activate + touch-base — docs/ios-session-implementation.md §Flow A).
    emitConflict(res.error.data);
    return false;
  }
  // Anything else (network blip, 5xx) is not a session verdict — keep going.
  return true;
}

/**
 * Session-verdict handling (Brief 025 §10, split by Brief 194). Reached for a
 * 401 + error code 20000 (token EXPIRED, and the refresh-and-retry inside
 * crm-client did not rescue it) or a 409 + error code 91049 (session DISPLACED
 * by another device). Stop the heartbeat, tell our own subscribers, and leave
 * the outbox ALONE — those rows re-flush after re-login.
 *
 * The router bounce is NOT our job: crm-client's `notifySessionInvalid(reason)`
 * already fired inside `request()`, and the app shell decides from the reason
 * whether to sign out now (displaced) or let the current item finish first
 * (expired).
 */
function handleDisplaced() {
  heartbeat?.stop();
  if (retryTimer !== null) { deps.timers.clearTimeout(retryTimer); retryTimer = null; }
  emitDisplaced();
}

// ---------------------------------------------------------------------------
// Push
// ---------------------------------------------------------------------------

/**
 * Queue one CRM row and schedule a debounced flush. This is the sink installed
 * into `sync-queue.js`, so every learner-store progress write reaches it.
 *
 * Order matters (Gotcha #3): the caller has ALREADY written locally by the time
 * we're here. We persist to the outbox first, and only then attempt the network
 * — so a flush failure can never lose the row.
 *
 * @param {"add"|"update"} endpoint
 * @param {string} table un-prefixed CRM table name
 * @param {Record<string, unknown>} row
 * @returns {Promise<void>}
 */
export async function queueRow(endpoint, table, row) {
  if (!isSyncableTable(table)) {
    console.warn(`[sync-engine] refusing to queue unsyncable table "${table}"`);
    return;
  }
  try {
    await enqueueOutboxRow({ endpoint, table, row });
  } catch (err) {
    console.warn("[sync-engine] outbox enqueue failed — row dropped", err);
    return;
  }
  scheduleDebouncedFlush();
}

function scheduleDebouncedFlush() {
  if (!initialised) return; // signed out: rows accumulate, flush on next login
  if (debounceTimer !== null) return;
  debounceTimer = deps.timers.setTimeout(() => {
    debounceTimer = null;
    void flush({ reason: "debounce" }).catch(() => {});
  }, deps.debounceMs);
}

/** @typedef {"debounce"|"breakpoint-exercise"|"breakpoint-lesson"|"visibility"|"pagehide"|"heartbeat"|"login"|"retry"|"reconcile"} FlushReason */

/** Reasons that pre-empt a pending debounce and flush right now (Gotcha #4). */
const BREAKPOINT_REASONS = new Set(["breakpoint-exercise", "breakpoint-lesson", "visibility", "pagehide", "login", "reconcile"]);

/** Reasons that must survive the tab being torn down → keepalive transport. */
const UNLOAD_REASONS = new Set(["visibility", "pagehide"]);

/**
 * Drain the outbox.
 *
 * Single-flight: a flush already in progress is returned rather than started
 * again, so a breakpoint landing mid-request can't double-send a batch.
 *
 * @param {{ reason: FlushReason }} params
 * @returns {Promise<{ sent: number, kept: number, skipped?: true }>}
 */
export function flush({ reason }) {
  if (BREAKPOINT_REASONS.has(reason) && debounceTimer !== null) {
    deps.timers.clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (inFlightFlush) return inFlightFlush;
  inFlightFlush = doFlush(reason).finally(() => { inFlightFlush = null; });
  return inFlightFlush;
}

/**
 * @param {FlushReason} reason
 * @returns {Promise<{ sent: number, kept: number, skipped?: true }>}
 */
async function doFlush(reason) {
  if (!isSignedIn()) return { sent: 0, kept: 0, skipped: true };

  const all = await getOutboxRows();
  if (all.length === 0) return { sent: 0, kept: 0 };

  const now = deps.now();
  // A breakpoint flush un-pauses exhausted rows and ignores backoff windows:
  // the learner just finished something, this is the moment to try hardest.
  const forced = BREAKPOINT_REASONS.has(reason);
  const due = all.filter((r) => forced || (!r.paused && (r.nextAttemptAt ?? 0) <= now));
  if (due.length === 0) return { sent: 0, kept: all.length };

  const keepalive = UNLOAD_REASONS.has(reason);

  // "Around each sync" heartbeat (Allen §4): confirm the session BEFORE sending
  // rows, so a displaced session fails one cheap call instead of a whole batch.
  // Skipped on the unload path — there is no budget for two round-trips there,
  // and the push itself surfaces a 401 just as well.
  if (!keepalive && !(await touchBase())) return { sent: 0, kept: all.length };

  let sent = 0;
  for (const endpoint of /** @type {const} */ (["add", "update"])) {
    const batch = due.filter((r) => r.endpoint === endpoint);
    if (batch.length === 0) continue;
    const ok = await sendBatch(endpoint, batch, { keepalive });
    if (ok === "sent") { sent += batch.length; continue; }
    if (ok === "abort") break; // displaced / conflict — stop, keep everything
  }

  const remaining = await getOutboxRows();
  return { sent, kept: remaining.length };
}

/**
 * Send one endpoint's batch. Returns "sent" on ack, "retry" when the rows were
 * kept for a later attempt, "abort" when the whole flush must stop (session
 * displaced, or a 91048 the learner has to resolve first).
 *
 * @param {"add"|"update"} endpoint
 * @param {any[]} batch
 * @param {{ keepalive: boolean }} opts
 * @returns {Promise<"sent"|"retry"|"abort">}
 */
async function sendBatch(endpoint, batch, { keepalive }) {
  // One request_id for the batch, pinned onto every row so a RETRY re-sends the
  // same id and the CRM can de-duplicate (iOS: `lastUploadRequestId`,
  // KFESyncUserDataMgr.m:110-113). Reuse an id a previous attempt already
  // minted rather than rolling a fresh one.
  const requestId = batch.find((r) => r.requestId)?.requestId ?? makeRequestId(deps.now);
  const learnerProgress = buildLearnerProgress(batch.map((r) => ({ table: r.table, row: r.row })));
  const body = buildUploadBody({ learnerProgress, requestId });

  const send = endpoint === "add" ? crmAddLearnerProgress : crmUpdateLearnerProgress;
  const res = await send(body, { keepalive });

  if (res.success) {
    await deleteOutboxRows(batch.map((r) => r.id));
    return "sent";
  }

  const kind = res.error?.kind;

  if (isSessionVerdict(kind)) {
    // Outbox preserved untouched — the rows re-flush after the learner
    // re-authenticates (Brief 025 §10).
    await pinRequestId(batch, requestId);
    handleDisplaced();
    return "abort";
  }

  if (kind === "progress_conflict") {
    // 91048 — another device has newer unpushed progress. The rows are still
    // valid intent, so they stay; the learner picks Wait / Accept Loss /
    // Reconcile and `resolveProgressConflict` acts on the outbox from there.
    await pinRequestId(batch, requestId);
    emitConflict(res.error?.data);
    return "abort";
  }

  if (kind === "bad_credentials" || kind === "validation_error" || kind === "account_locked") {
    // Semantic rejections. Retrying identical bytes cannot change the verdict
    // (Brief 025 §12), so pause the rows with the reason recorded rather than
    // burning the backoff schedule on them.
    await markBatch(batch, { requestId, paused: true, lastError: `${kind}: ${res.error?.message ?? ""}` });
    return "retry";
  }

  // network / 5xx / rate_limited / generic → transient. Back off.
  await backOff(batch, requestId, `${kind ?? "unknown"}: ${res.error?.message ?? ""}`);
  return "retry";
}

/** Persist the batch's request_id without otherwise touching the rows. */
async function pinRequestId(batch, requestId) {
  for (const row of batch) {
    if (row.requestId === requestId) continue;
    await putOutboxRow({ ...row, requestId });
  }
}

/** @param {any[]} batch @param {Partial<any>} patch */
async function markBatch(batch, patch) {
  for (const row of batch) await putOutboxRow({ ...row, ...patch });
}

/**
 * Bump `attempts`, compute the next window from RETRY_DELAYS_MS, and arm a
 * single timer for the soonest one. Past MAX_ATTEMPTS the row parks as `paused`
 * — still in the outbox, still the learner's intent, just no longer spinning.
 * @param {any[]} batch
 * @param {number} requestId
 * @param {string} lastError
 */
async function backOff(batch, requestId, lastError) {
  const now = deps.now();
  let soonest = Infinity;
  for (const row of batch) {
    const attempts = (row.attempts ?? 0) + 1;
    const exhausted = attempts >= MAX_ATTEMPTS;
    const delay = RETRY_DELAYS_MS[Math.min(attempts, RETRY_DELAYS_MS.length - 1)];
    const nextAttemptAt = exhausted ? 0 : now + delay;
    if (!exhausted) soonest = Math.min(soonest, delay);
    await putOutboxRow({ ...row, requestId, attempts, lastError, paused: exhausted, nextAttemptAt });
  }
  if (soonest === Infinity) return;
  if (retryTimer !== null) deps.timers.clearTimeout(retryTimer);
  retryTimer = deps.timers.setTimeout(() => {
    retryTimer = null;
    void flush({ reason: "retry" }).catch(() => {});
  }, soonest);
}

// ---------------------------------------------------------------------------
// Pull
// ---------------------------------------------------------------------------

/**
 * Fetch the server's delta and fold it in.
 *
 * `crmQueryLearnerProgress` is an advisory pre-check (iOS uses it to size the
 * download and skip it entirely when there is nothing new). We treat "can't
 * tell" as "yes, there is something" — download is idempotent and the merge is
 * monotone, so an unnecessary pull costs a round-trip, while a wrongly-skipped
 * one costs the learner their cross-device progress.
 *
 * @param {{ reason: "login"|"heartbeat"|"manual"|"reconcile" }} params
 * @returns {Promise<{ pulled: boolean, merged?: Record<string, number>, skipped?: Record<string, number> }>}
 */
export async function pull({ reason }) {
  if (!isSignedIn()) return { pulled: false };

  const state = await getSyncState();
  // download_type: no local cursor → 1 (fresh install / full pull),
  //                with a cursor → 0 (normal delta). Mirrors iOS
  //                `KFESyncUserDataMgr.m:143-160` (fresh-install branch vs
  //                normal-with-cursor branch). Applied to BOTH the query
  //                and the download calls; iOS uses the same param dict for
  //                both.
  const downloadType = state.lastServerSince != null ? 0 : 1;
  const query = await crmQueryLearnerProgress({ downloadType });
  if (!query.success) {
    if (isSessionVerdict(query.error?.kind)) handleDisplaced();
    return { pulled: false };
  }
  if (hasNothingNew(query.data)) return { pulled: false };

  const since = state.lastServerSince;
  const res = await crmDownloadLearnerProgress(since != null ? { since, downloadType } : { downloadType });
  if (!res.success) {
    if (isSessionVerdict(res.error?.kind)) handleDisplaced();
    return { pulled: false };
  }

  // Wire shape (2026-09-14 live-verified): the download endpoint wraps
  // `learner_progress` inside `data.result`, alongside `more_data`, `last_download`
  // etc. Preserve the flat-`data.learner_progress` and camelCase fallbacks in
  // case other endpoints keep the old shape.
  const learnerProgressRaw = res.data?.result?.learner_progress
    ?? res.data?.result?.learnerProgress
    ?? res.data?.learner_progress
    ?? res.data?.learnerProgress;
  const parsed = parseDownloadedProgress(learnerProgressRaw);
  const result = await mergeDownloadedProgress(parsed, learnerStore);

  const pulledAt = deps.now();
  const nextSince = res.data?.last_updated_on ?? res.data?.since ?? Math.floor(pulledAt / 1000);
  await setSyncState({ lastSyncAt: pulledAt, lastServerSince: nextSince });

  // Advance the server's "we agree you're synced up to N" pointer. A failure
  // here is harmless (Gotcha #10): the next pull just re-fetches some rows, and
  // the merge is idempotent.
  const ack = await crmUpdateLastSync({ since: nextSince });
  if (!ack.success && isSessionVerdict(ack.error?.kind)) handleDisplaced();

  console.info(`[sync-engine] pull(${reason}) merged`, result.merged, "skipped", result.skipped);
  return { pulled: true, merged: result.merged, skipped: result.skipped };
}

/**
 * Whether the query response positively says there is nothing to download.
 * Tolerant of several plausible field names because the exact wire shape is
 * unconfirmed until the live smoke (Brief 188 §"Pre-flight status" carries the
 * same caveat for the rest of the surface). Anything we don't recognise reads
 * as "there might be something" — the safe direction.
 * @param {any} data
 * @returns {boolean}
 */
function hasNothingNew(data) {
  if (!data || typeof data !== "object") return false;
  for (const key of ["is_have_new_update", "has_new_update", "hasNewUpdate", "has_updates"]) {
    if (key in data) return !data[key];
  }
  for (const key of ["data_size", "dataSize", "size", "total"]) {
    if (key in data) return Number(data[key]) === 0;
  }
  return false;
}

// ---------------------------------------------------------------------------
// 91048 conflict resolution (Brief 190 seam)
// ---------------------------------------------------------------------------

/**
 * Act on the learner's answer to a 91048 device-switch prompt. Brief 190's
 * modal calls this; until 190 lands nothing does, and the outbox simply waits
 * (the "wait" branch by default — see `emitConflict`).
 *
 * @param {"wait"|"accept_loss"|"reconcile"} choice
 * @returns {Promise<{ ok: boolean, choice: string }>}
 */
export async function resolveProgressConflict(choice) {
  if (choice === "wait") {
    // Tell the server we're holding for the other device. Outbox untouched; the
    // next breakpoint or heartbeat retries the push.
    const ok = await touchBase({ sessionResolution: "wait" });
    return { ok, choice };
  }

  if (choice === "accept_loss") {
    // The learner has declared the other device stale and THIS one the source
    // of truth. Only on a confirmed server ack may the outbox be dropped —
    // dropping it on a failed call would destroy progress for nothing.
    const res = await crmAcceptLoss();
    if (!res.success) {
      if (isSessionVerdict(res.error?.kind)) handleDisplaced();
      return { ok: false, choice };
    }
    await clearOutbox();
    pendingConflict = null;
    return { ok: true, choice };
  }

  if (choice === "reconcile") {
    // Server merges the two devices. Re-pull so local matches the merged
    // state, THEN re-push what we still hold.
    const ok = await touchBase({ sessionResolution: "reconcile" });
    if (!ok) return { ok: false, choice };
    await pull({ reason: "reconcile" });
    await flush({ reason: "reconcile" });
    pendingConflict = null;
    return { ok: true, choice };
  }

  throw new Error(`[sync-engine] unknown conflict choice: ${choice}`);
}

/** The unresolved 91048 payload, if any. For Brief 190's modal to hydrate from. */
export function getPendingConflict() {
  return pendingConflict;
}

// ---------------------------------------------------------------------------
// Content-version awareness (Brief 025 §13)
// ---------------------------------------------------------------------------

/**
 * Ask the CRM whether newer content exists. Called once after login and again
 * on tab focus once the last check is more than an hour old.
 *
 * NEVER reloads mid-session (Gotcha #8) — an in-progress exercise would be
 * destroyed. It sets a localStorage flag; the next pathway load reads it.
 *
 * Cursor semantics (Brief 025 §CRM contract #2): send `since` as the newest
 * package timestamp we hold; read `data.cup[]`; advance to
 * `max(published_date)` across the entries. An empty `cup[]` advances nothing.
 *
 * @returns {Promise<{ checked: boolean, stale?: boolean }>}
 */
export async function checkContentUpdate() {
  if (!isSignedIn()) return { checked: false };
  const now = deps.now();
  if (now - lastContentCheckAt < CONTENT_CHECK_INTERVAL_MS && lastContentCheckAt !== 0) {
    return { checked: false };
  }
  lastContentCheckAt = now;

  const state = await getSyncState();
  const since = state.lastContentSince;
  const res = await crmContentUpdate(since != null ? { since } : {});
  if (!res.success) {
    if (isSessionVerdict(res.error?.kind)) handleDisplaced();
    return { checked: false };
  }

  const cup = Array.isArray(res.data?.cup) ? res.data.cup : [];
  if (cup.length === 0) return { checked: true, stale: false };

  const newest = cup.reduce((/** @type {number} */ max, /** @type {any} */ entry) => {
    const p = Number(entry?.published_date);
    return Number.isFinite(p) && p > max ? p : max;
  }, 0);
  if (newest <= 0) return { checked: true, stale: false };
  if (since != null && newest <= Number(since)) return { checked: true, stale: false };

  await setSyncState({ lastContentSince: newest });
  try { localStorage.setItem(CONTENT_STALE_KEY, String(newest)); }
  catch { /* private mode — the flag is a nicety, not a correctness requirement */ }
  return { checked: true, stale: true };
}

/** Whether a content refresh is pending. Read by the pathway load path. */
export function isContentStale() {
  try { return localStorage.getItem(CONTENT_STALE_KEY) !== null; }
  catch { return false; }
}

/** Clear the stale flag once the pathway has re-fetched. */
export function clearContentStale() {
  try { localStorage.removeItem(CONTENT_STALE_KEY); }
  catch { /* no-op */ }
}

// ---------------------------------------------------------------------------
// Sink installation — see the CYCLE AVOIDANCE note in the header.
// ---------------------------------------------------------------------------

setQueueSink((endpoint, table, row) => {
  // Fire-and-forget on purpose: `queueProgressRow` is called from inside
  // learner-store writes that must not be slowed down (or broken) by the sync
  // path. Errors are already swallowed inside queueRow.
  void queueRow(endpoint, table, row);
});

setBreakpointSink((reason) => {
  // The queueRow that precedes a breakpoint is itself async (it awaits the IDB
  // enqueue), so flushing in the same tick would race it and send an empty
  // batch. A microtask-plus-tick of slack lets the enqueue land first; the
  // flush still happens far sooner than the 2s debounce it pre-empts.
  const flushReason = reason === "breakpoint-settings" ? "breakpoint-exercise" : reason;
  deps.timers.setTimeout(() => {
    void flush({ reason: /** @type {FlushReason} */ (flushReason) }).catch(() => {});
  }, 0);
});

/** Test-only: drop all module state between cases. Never called in production. */
export function __resetSyncEngineForTests() {
  teardownSync();
  displacedListeners = [];
  conflictListeners = [];
  lastContentCheckAt = 0;
  deps = { ...DEFAULTS };
}
